/**
 * 记一笔收款。
 * 金额默认预填当前余额 —— 这是最常见的情况（顾客来结清）。
 * 超额需要二次确认：顾客付整钞找零很常见，但也可能是记错了。
 */

import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type PaymentKind, type PaymentMethod } from '../lib/api'
import { Button, Drawer, Field, MoneyInput, Segmented, Textarea, useToast } from '../components/ui'
import { parseAmount, rm } from '../lib/format'
import { feedback } from '../lib/feedback'

export function PaymentDrawer({
  open,
  onClose,
  caseId,
  balanceAmount,
  onDone,
}: {
  open: boolean
  onClose: () => void
  caseId: string
  balanceAmount: number
  onDone?: (r: { balanceAmount: number; suggestion: { code: string; message: string } | null }) => void
}) {
  const qc = useQueryClient()
  const toast = useToast()

  const [amount, setAmount] = useState('')
  const [kind, setKind] = useState<PaymentKind>('FINAL')
  const [method, setMethod] = useState<PaymentMethod>('CASH')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [confirmOverpay, setConfirmOverpay] = useState(false)

  useEffect(() => {
    if (!open) return
    setAmount(balanceAmount > 0 ? (balanceAmount / 100).toFixed(2) : '')
    setKind(balanceAmount > 0 ? 'FINAL' : 'INSTALLMENT')
    setMethod('CASH')
    setNote('')
    setError(null)
    setConfirmOverpay(false)
  }, [open, balanceAmount])

  const sen = parseAmount(amount)
  const overpay = sen !== null && kind !== 'REFUND' && sen > balanceAmount

  const save = useMutation({
    mutationFn: () => {
      if (sen === null || sen <= 0) throw new Error('请填写正确的金额')
      return api.post<{
        balanceAmount: number
        paidAmount: number
        suggestion: { code: string; message: string } | null
      }>(`/api/cases/${caseId}/payments`, {
        kind,
        method,
        amount: sen,
        note: note.trim() || null,
        allowOverpay: confirmOverpay,
      })
    },
    onSuccess: (r) => {
      feedback('money')
      void qc.invalidateQueries({ queryKey: ['case', caseId] })
      void qc.invalidateQueries({ queryKey: ['today'] })
      void qc.invalidateQueries({ queryKey: ['finance'] })
      // 把结果算给她看，省一次心算
      toast.show(`已收 ${rm(sen!)} · 余额 ${rm(r.balanceAmount)}`)
      onDone?.(r)
      onClose()
    },
    onError: (e) => {
      feedback('error')
      setError((e as Error).message)
    },
  })

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="记一笔收款"
      footer={
        <>
          <Button size="lg" onClick={onClose}>
            取消
          </Button>
          <Button
            size="lg"
            variant="primary"
            loading={save.isPending}
            disabled={sen === null || sen <= 0 || (overpay && !confirmOverpay)}
            onClick={() => save.mutate()}
          >
            保存
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {error && (
          <div className="rounded-md border border-bad-line bg-bad-bg px-4 py-3 text-[16px] text-bad">
            {error}
          </div>
        )}

        <div className="tnum rounded-md bg-sunken px-4 py-3 text-[17px]">
          当前余额 <span className="font-bold">{rm(balanceAmount)}</span>
        </div>

        <Field label="金额" required>
          <MoneyInput autoFocus value={amount} onChange={setAmount} invalid={!!amount && sen === null} />
        </Field>

        {overpay && (
          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-warn-line bg-warn-bg px-4 py-3">
            <input
              type="checkbox"
              checked={confirmOverpay}
              onChange={(e) => setConfirmOverpay(e.target.checked)}
              className="mt-1 size-5.5 shrink-0 accent-warn"
            />
            <span className="text-[16px] text-warn">
              金额超过余额 {rm(sen! - balanceAmount)}，确认要这样记吗？
            </span>
          </label>
        )}

        <Field label="类型">
          <Segmented
            value={kind}
            onChange={setKind}
            ariaLabel="收款类型"
            options={[
              { value: 'DEPOSIT', label: '定金' },
              { value: 'INSTALLMENT', label: '分期' },
              { value: 'FINAL', label: '尾款' },
              { value: 'REFUND', label: '退款' },
            ]}
          />
        </Field>

        <Field label="方式">
          <Segmented
            value={method}
            onChange={setMethod}
            ariaLabel="收款方式"
            options={[
              { value: 'CASH', label: '现金' },
              { value: 'DUITNOW', label: 'DuitNow' },
              { value: 'TRANSFER', label: '转账' },
              { value: 'OTHER', label: '其他' },
            ]}
          />
        </Field>

        <Field label="备注" hint="例：家属代付">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
        </Field>
      </div>
    </Drawer>
  )
}
